import { useEffect, useState } from "react";
import { FormikHelpers, useFormik } from "formik";
import TextInput from "../components/basicComponents/text-input";
import TextArea from "../components/basicComponents/text-area";
import ImageUpload from "../components/basicComponents/image-upload";
import Container from "../components/basicComponents/container";
import TextEditor from "../components/basicComponents/text-editor";
import Select from "../components/basicComponents/select";
import * as Icons from "../assets/SVGs/index";
import Button from "../components/basicComponents/button";
import { useSelector } from "react-redux";
import { selectGlobalState } from "../state-management/selectors";
import { ArticleDataProps, ArticleComponentProps } from "../types/public";
import ToggleSwitch from "./basicComponents/switch-box";
import { articleValidationSchema } from "../validation/yup-schema";

export default function ArticleComponent({
  handleSubmit,
  formData,
  currentUserId,
  loading,
}: ArticleComponentProps) {
  const { departmentsData, groupingData } = useSelector(selectGlobalState);
  const [departmentOptions, setDepartmetOptions] = useState([
    { label: "", value: "" },
  ]);
  const permissionDepartment = groupingData.find(
    (item: any) => item.user_id === currentUserId
  )?.department_id || ["none"];

  useEffect(() => {
    setDepartmetOptions(
      departmentsData
        .filter((item: any) => permissionDepartment.includes(item._id))
        .map((art: any) => ({
          label: art.name,
          value: art._id,
        }))
    );
  }, [departmentsData, permissionDepartment]);

  const formik = useFormik<ArticleDataProps>({
    initialValues: formData,
    enableReinitialize: true,
    validationSchema: articleValidationSchema,
    onSubmit: async (
      values,
      { setSubmitting, resetForm }: FormikHelpers<ArticleDataProps>
    ) => {
      try {
        const result = await handleSubmit(values);
        if (result) {
          resetForm();
          localStorage.removeItem("articleImageBase64");
        }
      } catch (error) {
        console.error("Error during submit:", error);
      } finally {
        setSubmitting(false);
      }
    },
  });

  const handleEditorBlur = (..._args: any[]) => {
    formik.setFieldTouched("content", true);
    console.log(_args);
  };

  return (
    <div className="flex flex-col items-center w-full">
      <Container header="ثبت مقاله  ">
        <form className="w-full" onSubmit={formik.handleSubmit}>
          <div className="flex flex-col md:flex-row gap-2">
            <div className="mb-5 mt-5 md:w-[33.3%] w-full">
              <TextInput
                label="عنوان مقاله"
                name="title"
                value={formik.values.title}
                placeholder="عنوان مقاله"
                onChange={formik.handleChange}
                onBlur={formik.handleBlur}
                RightIcon={<Icons.TitleIcon className="w-5 h-5" />}
                helperText={
                  formik.touched.title && formik.errors.title
                    ? formik.errors.title
                    : undefined
                }
              />
            </div>

            <div className="mb-5 mt-5 md:w-[33.3%] w-full">
              <TextInput
                label="نوشته شده توسط"
                name="author"
                value={formik.values.author}
                placeholder="نوشته شده توسط "
                onChange={formik.handleChange}
                onBlur={formik.handleBlur}
                RightIcon={<Icons.AuthorIcon className="w-5 h-5" />}
                helperText={
                  formik.touched.author && formik.errors.author
                    ? formik.errors.author
                    : undefined
                }
              />
            </div>

            <div className="mb-5 mt-5 md:w-[33.3%] w-full">
              <Select
                name="department_id"
                options={departmentOptions}
                value={formik.values.department_id}
                onChange={formik.handleChange}
                onBlur={formik.handleBlur}
                placeholder="انتخاب دسته بندی"
                className="border"
                error={
                  formik.touched.department_id && formik.errors.department_id
                    ? formik.errors.department_id
                    : undefined
                }
              />
            </div>
          </div>

          <div className="flex flex-col md:flex-row items-start gap-2">
            <div className="md:w-[33.3%] w-full">
              <TextArea
                name="tags"
                value={formik.values.tags}
                placeholder="تگ های مقاله"
                onChange={formik.handleChange}
                onBlur={formik.handleBlur}
                rows={4}
                error={
                  formik.touched.tags && formik.errors.tags
                    ? formik.errors.tags
                    : undefined
                }
              />
            </div>
            <div className="md:w-[33.3%] w-full">
              <ImageUpload
                name="index_picture"
                onImageChange={(file) => {
                  if (file) {
                    const reader = new FileReader();
                    reader.onloadend = () => {
                      const base64String = reader.result as string;
                      formik.setFieldValue("index_picture", base64String);
                      localStorage.setItem("articleImageBase64", base64String);
                    };
                    reader.readAsDataURL(file);
                  } else {
                    formik.setFieldValue("index_picture", "");
                    localStorage.removeItem("articleImageBase64");
                  }
                }}
                onBlur={formik.handleBlur}
                storageKey="articleImageBase64"
                defaultImage={formik.values.index_picture || ""}
                helperText={
                  formik.touched.index_picture && formik.errors.index_picture
                    ? formik.errors.index_picture
                    : undefined
                }
              />
            </div>

            <div className="md:w-[33.3%] w-full">
              <ToggleSwitch
                label="وضعیت انتشار"
                checked={formik.values.is_active ?? false}
                onChange={() => {
                  formik.setFieldValue("is_active", !formik.values.is_active);
                }}
              />
            </div>
          </div>
          <TextArea
            className="mt-3"
            name="description"
            value={formik.values.description}
            placeholder="توضیحات "
            onChange={formik.handleChange}
            onBlur={formik.handleBlur}
            rows={4}
            error={
              formik.touched.description && formik.errors.description
                ? formik.errors.description
                : undefined
            }
          />
          <TextEditor
            value={formik.values.content}
            onChange={(content) => {
              formik.setFieldValue("content", content);
            }}
            onBlur={handleEditorBlur}
            error={
              formik.touched.content && formik.errors.content
                ? formik.errors.content
                : undefined
            }
          />

          <div className="flex justify-end">
            <Button
              disabled={loading}
              type="submit"
              title={loading ? "...انتظار" : "ثبت"}
              variant="success"
              bgColor="#9EE1D1"
              borderColor="#00B087"
              color="#00B087"
            />
          </div>
        </form>
      </Container>
    </div>
  );
}
